import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Users, Building2, FileText, ClipboardCheck, TrendingUp, AlertCircle } from 'lucide-react';

interface StatCardProps {
  title: string;
  value: number;
  change?: number;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}

const StatCard = ({ title, value, change, icon: Icon, color }: StatCardProps) => (
  <div className="bg-[#18181b] rounded-xl border border-white/5 p-5">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-zinc-500 mb-1">{title}</p>
        <p className="text-2xl font-bold text-white">{value}</p>
        {change != null && (
          <p className={`text-xs mt-1 ${change > 0 ? 'text-emerald-400' : 'text-zinc-500'}`}>
            {change > 0 ? '+' : ''}{change}% from last week
          </p>
        )}
      </div>
      <div className={`p-3 rounded-lg ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
  </div>
);

export default function AdminDashboard() {
  const { user } = useAuth();

  // Mock stats - will be replaced with API data
  const stats = {
    totalUsers: 3,
    totalPitches: 0,
    pendingReviews: 0,
    vendors: 1,
    merchants: 1,
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-dashboard">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-rose-500/10 to-rose-600/5 rounded-2xl p-6 border border-rose-500/20">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white mb-2">Admin Dashboard</h1>
              <p className="text-zinc-400">
                Overview of platform activity and pending tasks.
              </p>
            </div>
            <div className="hidden sm:block">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-400 text-sm font-medium">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                System Online
              </span>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard
            title="Total Users"
            value={stats.totalUsers}
            icon={Users}
            color="bg-white/5 text-zinc-400"
          />
          <StatCard
            title="Vendors"
            value={stats.vendors}
            icon={Building2}
            color="bg-rose-500/10 text-rose-400"
          />
          <StatCard
            title="Merchants"
            value={stats.merchants}
            icon={Building2}
            color="bg-emerald-500/10 text-emerald-400"
          />
          <StatCard
            title="Total Pitches"
            value={stats.totalPitches}
            icon={FileText}
            color="bg-blue-500/10 text-blue-400"
          />
          <StatCard
            title="Pending Reviews"
            value={stats.pendingReviews}
            icon={ClipboardCheck}
            color="bg-rose-500/10 text-rose-400"
          />
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pending Reviews */}
          <div className="bg-[#18181b] rounded-xl border border-white/5 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Pending Reviews</h2>
              <a href="/admin/pitches" className="text-sm text-rose-400 hover:text-rose-300 font-medium">
                Open queue
              </a>
            </div>
            {stats.pendingReviews > 0 ? (
              <div className="space-y-3">
                {/* Placeholder for pending reviews list */}
              </div>
            ) : (
              <div className="text-center py-8 text-zinc-500">
                <ClipboardCheck className="w-12 h-12 mx-auto mb-3 text-zinc-700" />
                <p>All caught up!</p>
                <p className="text-sm">No pitches pending review</p>
              </div>
            )}
          </div>

          {/* Quick Stats */}
          <div className="bg-[#18181b] rounded-xl border border-white/5 p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
            <div className="space-y-3">
              <a
                href="/admin/analytics"
                data-testid="quick-action-analytics"
                className="flex items-center gap-4 p-4 rounded-lg border border-white/5 hover:border-rose-500/30 hover:bg-white/5 transition-colors group"
              >
                <div className="p-3 rounded-lg bg-white/5 text-zinc-400 group-hover:bg-rose-500/10 group-hover:text-rose-400">
                  <TrendingUp className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium text-white">View Analytics</p>
                  <p className="text-sm text-zinc-500">Inspect funnel and vendor performance</p>
                </div>
              </a>
              <a
                href="/admin/pitches"
                data-testid="quick-action-pitches"
                className="flex items-center gap-4 p-4 rounded-lg border border-white/5 hover:border-rose-500/30 hover:bg-white/5 transition-colors group"
              >
                <div className="p-3 rounded-lg bg-white/5 text-zinc-400 group-hover:bg-rose-500/10 group-hover:text-rose-400">
                  <FileText className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium text-white">View All Pitches</p>
                  <p className="text-sm text-zinc-500">Monitor all pitch submissions</p>
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* System Alerts */}
        <div className="bg-[#18181b] border border-white/5 rounded-xl p-4">
          <div className="flex gap-3">
            <div className="flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-zinc-500" />
            </div>
            <div>
              <p className="text-sm font-medium text-zinc-300">System Status</p>
              <p className="text-sm text-zinc-500 mt-1">
                All services are operating normally. AI evaluation engine is connected and ready.
              </p>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
